import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { useNoIndex } from "@/lib/noIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", location: "", kind: "Tempo pieno", description: "", open: true };
const inp = "mt-1 w-full border border-white/15 bg-[#1B1816] px-3 py-2.5 text-sm text-[#F3EFE6] outline-none focus:border-[#D98A52]";

const JobFields = ({ v, set, id }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    <label className="text-xs text-[#D6D0C4] sm:col-span-2">Titolo<input value={v.title} onChange={(e) => set({ ...v, title: e.target.value })} className={inp} data-testid={`job-title-${id}`} /></label>
    <label className="text-xs text-[#D6D0C4]">Sede<input value={v.location} onChange={(e) => set({ ...v, location: e.target.value })} className={inp} data-testid={`job-location-${id}`} /></label>
    <label className="text-xs text-[#D6D0C4]">Contratto<input value={v.kind} onChange={(e) => set({ ...v, kind: e.target.value })} className={inp} data-testid={`job-kind-${id}`} /></label>
    <label className="text-xs text-[#D6D0C4] sm:col-span-2">Descrizione<textarea rows={3} value={v.description} onChange={(e) => set({ ...v, description: e.target.value })} className={inp} data-testid={`job-desc-${id}`} /></label>
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.open} onChange={(e) => set({ ...v, open: e.target.checked })} className="h-4 w-4 accent-[#C26D38]" data-testid={`job-open-${id}`} /> Visibile nel sito (posizione aperta)</label>
  </div>
);

const JobRow = ({ job, call }) => {
  const [v, setV] = useState(job);
  return (
    <li className="border border-white/10 p-5" data-testid={`job-row-${job.id}`}>
      <JobFields v={v} set={setV} id={job.id} />
      <div className="mt-4 flex gap-3">
        <button onClick={() => call("put", `/${job.id}`, v)} className="btn-amber" data-testid={`job-save-${job.id}`}><Save className="h-4 w-4" /> Salva</button>
        <button onClick={() => window.confirm("Eliminare questa posizione?") && call("delete", `/${job.id}`)} className="btn-ghost" data-testid={`job-delete-${job.id}`}><Trash2 className="h-4 w-4" /> Elimina</button>
      </div>
    </li>
  );
};

// Owner page (link sent by email): add, edit, close or delete the open positions of "Lavora con noi".
export default function JobsManage() {
  const { token } = useParams();
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  useNoIndex();
  useEffect(() => {
    axios.get(`${API}/jobs/manage/${token}`).then((r) => setList(Array.isArray(r.data?.jobs) ? r.data.jobs : [])).catch(() => setList(false));
  }, [token]);
  const call = async (method, path = "", body) => {
    try {
      const r = await axios({ method, url: `${API}/jobs/manage/${token}${path}`, data: body });
      setList(r.data?.jobs ?? []);
      toast.success("Salvato");
      return true;
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Controlla i campi (titolo di almeno 3 lettere)");
      return false;
    }
  };
  const add = async () => { if (await call("post", "", draft)) setDraft(EMPTY); };
  return (
    <div className="min-h-screen bg-[#121110] text-[#F3EFE6]" data-testid="jobs-manage-page">
      <Nav />
      <main className="mx-auto max-w-3xl px-5 pb-28 pt-44 sm:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Posizioni aperte</h1>
        <p className="mt-4 text-base text-[#D6D0C4]">Le posizioni visibili compaiono nella pagina Lavora con noi, ognuna con il pulsante "Candidati".</p>
        {list === null && <Loader2 className="mt-10 h-6 w-6 animate-spin" />}
        {list === false && <p className="mt-10 text-lg" data-testid="jobs-manage-invalid">Link scaduto o non valido. Richiedine uno nuovo dall'Area riservata.</p>}
        {Array.isArray(list) && (
          <>
            <ul className="mt-10 space-y-4">{list.map((j) => <JobRow key={`${j.id}-${j.title}-${j.open}`} job={j} call={call} />)}</ul>
            <section className="mt-12 border-t border-white/10 pt-8" data-testid="job-new">
              <h2 className="mb-4 font-display text-2xl font-bold">Nuova posizione</h2>
              <JobFields v={draft} set={setDraft} id="new" />
              <button onClick={add} className="btn-amber mt-5" data-testid="job-add"><Plus className="h-4 w-4" /> Aggiungi posizione</button>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
